'use server'

import {
    Agenda,
    AgendaEvent,
    AgendaEventType,
    AgendaSetting,
    AgendaType,
} from '../common/interfaces/agenda.interface'
import { apiRequest, ApiResponse } from '../request'

// Re-export interfaces
export type { Agenda, AgendaEvent, AgendaSetting, AgendaType }

export async function getAgendas(
    professionalId?: number
): Promise<ApiResponse<Agenda[]>> {
    return apiRequest<Agenda[]>('agendas', {
        params: professionalId ? { professionalId } : {},
    })
}

export async function getAgenda(id: number): Promise<ApiResponse<Agenda>> {
    return apiRequest<Agenda>(`agendas/${id}`, {})
}

export async function getAgendasByProfessional(
    professionalId: number
): Promise<ApiResponse<Agenda>> {
    return apiRequest<Agenda>(`agendas/professional/${professionalId}`, {})
}

export async function getMyAgenda(): Promise<ApiResponse<Agenda>> {
    return apiRequest<Agenda>('agendas/my', {})
}

export async function getCompanyAgenda(): Promise<ApiResponse<Agenda>> {
    return apiRequest<Agenda>('agendas/company', {})
}

export async function createAgenda(data: {
    professionalId?: number
    type?: AgendaType
    userId?: number
    companyId?: number
    partnerId?: number
}): Promise<ApiResponse<Agenda>> {
    return apiRequest<Agenda>('agendas', { data }, 'post')
}

export interface UpdateAgendaData {
    professionalId?: number
    slotDuration?: number
    bufferTime?: number
    maxOverbooking?: number
    arrivalOrder?: boolean
    workingHours?: string
}

export async function updateAgenda(
    id: number,
    data: UpdateAgendaData
): Promise<ApiResponse<Agenda>> {
    return apiRequest<Agenda>(`agendas/${id}`, { data }, 'patch')
}

export async function deleteAgenda(id: number): Promise<ApiResponse<void>> {
    return apiRequest<void>(`agendas/${id}`, {}, 'delete')
}

export async function getAgendaEvents(
    agendaId?: number,
    clientId?: number
): Promise<ApiResponse<AgendaEvent[]>> {
    return apiRequest<AgendaEvent[]>('agenda-events', {
        params: {
            ...(agendaId && { agendaId }),
            ...(clientId && { clientId }),
        },
    })
}

export async function getAgendaEvent(
    id: number
): Promise<ApiResponse<AgendaEvent>> {
    return apiRequest<AgendaEvent>(`agenda-events/${id}`, {})
}

export async function getAgendaEventsByAgenda(
    agendaId: number
): Promise<ApiResponse<AgendaEvent[]>> {
    return apiRequest<AgendaEvent[]>(`agenda-events/agenda/${agendaId}`, {})
}

export async function getAgendaEventsByClient(
    clientId: number
): Promise<ApiResponse<AgendaEvent[]>> {
    return apiRequest<AgendaEvent[]>(`agenda-events/client/${clientId}`, {})
}

/**
 * Janela de datas das listagens da agenda. Sem ela o servidor devolve TODOS os
 * eventos — o que era tolerável com centenas de registros e deixou de ser depois
 * que os atendimentos importados passaram a ter evento na agenda.
 */
export interface AgendaEventRange {
    startDate: string
    endDate: string
}

export async function getAgendaEventsByProfessional(
    professionalId: number,
    range?: AgendaEventRange
): Promise<ApiResponse<AgendaEvent[]>> {
    return apiRequest<AgendaEvent[]>(
        `agenda-events/professional/${professionalId}`,
        { params: range }
    )
}

export async function getOverdueAgendaEvents(): Promise<
    ApiResponse<AgendaEvent[]>
> {
    return apiRequest<AgendaEvent[]>('agenda-events/overdue', {})
}

export async function resolveAgendaEvent(
    id: number,
    status: 'COMPLETED' | 'NO_SHOW' | 'NO_SHOW_JUSTIFIED' | 'CANCELED',
): Promise<ApiResponse<AgendaEvent>> {
    return apiRequest<AgendaEvent>(
        `agenda-events/${id}/resolve`,
        { data: { status } },
        'patch',
    )
}

export async function getMyAgendaEvents(
    range?: AgendaEventRange
): Promise<ApiResponse<AgendaEvent[]>> {
    return apiRequest<AgendaEvent[]>('agenda-events/my', { params: range })
}

export async function getCompanyAgendaEvents(
    range?: AgendaEventRange
): Promise<ApiResponse<AgendaEvent[]>> {
    return apiRequest<AgendaEvent[]>('agenda-events/company', { params: range })
}

/** Eventos da empresa filtrados por tipo — abas Procedimentos/Consultas/Exames. */
export async function getCompanyAgendaEventsByType(
    type: 'PROCEDURE' | 'CONSULTATION' | 'EXAM',
    range?: AgendaEventRange
): Promise<ApiResponse<AgendaEvent[]>> {
    return apiRequest<AgendaEvent[]>('agenda-events/company/by-type', {
        params: { type, ...(range || {}) },
    })
}

export interface CreateAgendaEventData {
    title: string
    description?: string
    agendaId: number
    clientId?: number
    roomId?: number
    orderId?: number
    consultationId?: number
    examId?: number
    partnerId?: number
    type?: AgendaEventType
    status?: 'PENDING' | 'CONFIRMED' | 'CANCELED' | 'COMPLETED'
    startDate: string
    endDate: string
    notes?: string
    color?: string
    notificationEmail?: string
    whatsappNotificationNumber?: string
    createEntity?: boolean
    professionalId?: number
    companyId?: number
}

export async function createAgendaEvent(
    data: CreateAgendaEventData
): Promise<ApiResponse<AgendaEvent>> {
    return apiRequest<AgendaEvent>('agenda-events', { data }, 'post')
}

export async function updateAgendaEvent(
    id: number,
    data: {
        title?: string
        description?: string
        agendaId?: number
        clientId?: number
        status?: 'PENDING' | 'CONFIRMED' | 'CANCELED' | 'COMPLETED'
        startDate?: string
        endDate?: string
    }
): Promise<ApiResponse<AgendaEvent>> {
    return apiRequest<AgendaEvent>(`agenda-events/${id}`, { data }, 'patch')
}

export async function deleteAgendaEvent(
    id: number
): Promise<ApiResponse<void>> {
    return apiRequest<void>(`agenda-events/${id}`, {}, 'delete')
}

export async function getAgendaSettings(
    agendaId?: number
): Promise<ApiResponse<AgendaSetting[]>> {
    return apiRequest<AgendaSetting[]>('agendas/settings', {
        params: agendaId ? { agendaId } : {},
    })
}

export async function getAgendaSetting(
    id: number
): Promise<ApiResponse<AgendaSetting>> {
    return apiRequest<AgendaSetting>(`agendas/settings/${id}`, {})
}

export async function getAgendaSettingsByAgenda(
    agendaId: number
): Promise<ApiResponse<AgendaSetting[]>> {
    return apiRequest<AgendaSetting[]>(`agendas/${agendaId}/settings`, {})
}

export async function createAgendaSetting(data: {
    agendaId: number
    name: string
    value: string
}): Promise<ApiResponse<AgendaSetting>> {
    return apiRequest<AgendaSetting>('agendas/settings', { data }, 'post')
}

export async function updateAgendaSetting(
    id: number,
    data: {
        agendaId?: number
        name?: string
        value?: string
    }
): Promise<ApiResponse<AgendaSetting>> {
    return apiRequest<AgendaSetting>(
        `agendas/settings/${id}`,
        { data },
        'patch'
    )
}

export async function deleteAgendaSetting(
    id: number
): Promise<ApiResponse<void>> {
    return apiRequest<void>(`agendas/settings/${id}`, {}, 'delete')
}

// Eventos Recorrentes

export type RecurringFrequency =
    | 'DAILY'
    | 'WEEKLY'
    | 'BIWEEKLY'
    | 'MONTHLY'
    | 'QUARTERLY'
    | 'SEMIANNUAL'
    | 'ANNUAL'

export interface CreateRecurringEventConfigData {
    agendaId: number
    title: string
    description?: string
    type?: string
    frequency: RecurringFrequency
    startTime: string
    endTime: string
    notificationEmail?: string
    whatsappNotificationNumber?: string
    clientId?: number
    roomId?: number
    partnerId?: number
    companyId?: number
    userId?: number
    nextOccurrence: string
}

export interface RecurringEventConfig {
    id: number
    agendaId: number
    title: string
    description?: string
    type?: string
    frequency: RecurringFrequency
    startTime: string
    endTime: string
    notificationEmail?: string
    whatsappNotificationNumber?: string
    clientId?: number
    roomId?: number
    partnerId?: number
    companyId?: number
    userId?: number
    nextOccurrence: string
    lastCreatedEventId?: number
    active: boolean
    createdAt: string
    updatedAt: string
}

export async function getRecurringEventConfigs(
    agendaId?: number
): Promise<ApiResponse<RecurringEventConfig[]>> {
    return apiRequest<RecurringEventConfig[]>('recurring-event-configs', {
        params: agendaId ? { agendaId } : {},
    })
}

export async function createRecurringEventConfig(
    data: CreateRecurringEventConfigData
): Promise<ApiResponse<RecurringEventConfig>> {
    return apiRequest<RecurringEventConfig>('recurring-event-configs', { data }, 'post')
}

export async function updateRecurringEventConfig(
    id: number,
    data: Partial<CreateRecurringEventConfigData> & { active?: boolean }
): Promise<ApiResponse<any>> {
    return apiRequest(`recurring-event-configs/${id}`, { data }, 'patch')
}

export async function deleteRecurringEventConfig(
    id: number
): Promise<ApiResponse<void>> {
    return apiRequest<void>(`recurring-event-configs/${id}`, {}, 'delete')
}
